import { useEffect, useState } from "react";
import { Printer, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ALL_LABEL_SIZES,
  buildSparePartPrintJobs,
  getWorkstationLabelPrintingSettings,
  isZebraEnvEnabled,
  isLabelPrinterName,
  labelSizeLabel,
  labelSizeOptionLabel,
  DEFAULT_LABEL_SIZE,
  PrintCancelledError,
  resolvePrinterChoice,
  setWorkstationLabelPrintingSettings,
  useLabelPrinter,
  useLabelPrintingSettings,
  type LabelPrintJob,
  type LabelSizeId,
  type WorkstationLabelPrintingSettings,
} from "@/labels";

const USE_COMPANY_DEFAULT = "__company_default__";
const AUTO_PICK = "__auto_pick__";

export function LabelPrintingWorkstationPanel() {
  const envEnabled = isZebraEnvEnabled();
  const { settings: companySettings, loading: companyLoading } = useLabelPrintingSettings();
  const {
    isZebraEnabled,
    isZebraAvailable,
    qzAvailable,
    probeQz,
    refreshPrinters,
    printJobs,
  } = useLabelPrinter();

  const [workstation, setWorkstation] = useState<WorkstationLabelPrintingSettings>(() =>
    getWorkstationLabelPrintingSettings(),
  );
  const [printers, setPrinters] = useState<string[]>([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setWorkstationLabelPrintingSettings(workstation);
  }, [workstation]);

  const effectiveSize = workstation.labelSizeOverride ?? companySettings.defaultLabelSize ?? DEFAULT_LABEL_SIZE;

  const handleRefresh = async () => {
    setLoadingPrinters(true);
    try {
      const list = await refreshPrinters();
      setPrinters(list);
      if (list.length === 0) {
        toast.info("QZ Tray connected but reported no printers");
      } else {
        toast.success(`Found ${list.length} printer${list.length === 1 ? "" : "s"}`);
      }
      const choice = resolvePrinterChoice({
        printers: list,
        stored: workstation.printerName,
        storedSource: workstation.printerSource,
      });
      setWorkstation((prev) => ({
        ...prev,
        printerName: choice.printer,
        printerSource: choice.source === "none" ? null : choice.source,
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not reach QZ Tray";
      toast.error(message);
      await probeQz();
    } finally {
      setLoadingPrinters(false);
    }
  };

  const runTestPrint = async (
    kind: "spare-part" | "storage-bin" | "passport",
  ) => {
    setTesting(true);
    try {
      const size = effectiveSize;
      const printerName = workstation.printerName ?? undefined;
      if (!printerName) {
        toast.error("No Zebra printer found — pick a printer first.");
        return;
      }
      let jobs: LabelPrintJob[];

      if (kind === "spare-part") {
        jobs = buildSparePartPrintJobs(
          [
            {
              id: "TEST-001",
              name: "Test Spare Part",
              serial_no: "TEST-PRINT",
              qr_token: "TESTTOKEN0001",
              brand: "FixID",
              model_number: "Workstation",
              harvested_at: new Date().toISOString(),
            },
          ],
          { size },
        );
      } else if (kind === "storage-bin") {
        jobs = [
          {
            kind,
            data: {
              qrValue: "A01-B1-L1-S1-B01",
              binCode: "A01-B1-L1-S1-B01",
              locationName: "FixID Storage Center",
              description: "Test Miele bin 03",
              footer: "Aisle 1 · Bay 1 · Lvl 1 · Shelf 1 · Bin 1",
            },
            options: { size },
          },
        ];
      } else {
        const origin = window.location.origin;
        jobs = [
          {
            kind: "passport",
            data: {
              qrValue: `${origin}/passport/TEST`,
              certifiedHeading: "FixID Certified",
              title: "AEG L8FB86ES washing machine",
              modelLine: "Model: L8FB86ES",
              categoryLine: "Category: Washing machine",
              completedLine: "Completed: Today",
              qualityGradeLine: "Quality grade: A",
              serialNumberHeading: "Serial number",
              serialNumber: "FIX-WM-2026-000184",
              scanHint: "Scan for product passport",
            },
            options: { size },
          },
        ];
      }

      await printJobs(jobs, printerName);
      setWorkstation(
        setWorkstationLabelPrintingSettings({ lastTestAt: new Date().toISOString() }),
      );
      toast.success("Test print sent");
    } catch (err: unknown) {
      if (err instanceof PrintCancelledError) return;
      const message = err instanceof Error ? err.message : "Test print failed";
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  if (!envEnabled) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 space-y-2">
        <div className="flex items-center gap-2">
          <Printer className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Label printing</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Zebra printing is turned off on this deployment. Labels will use the browser print
          dialog. Ask your platform admin to enable Zebra printing on a workstation build.
        </p>
      </div>
    );
  }

  if (companyLoading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="animate-pulse h-5 w-40 bg-muted rounded" />
      </div>
    );
  }

  if (!isZebraEnabled) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 space-y-2">
        <div className="flex items-center gap-2">
          <Printer className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Label printing</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Your company has Zebra printing disabled. Labels will use the browser print dialog.
          Company admins can enable it in Company Settings → Label printing.
        </p>
      </div>
    );
  }

  const canTest = testing ? false : true;

  return (
    <div className="bg-card rounded-xl border border-border p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Printer className="w-4 h-4 text-muted-foreground" />
          <div>
            <h3 className="font-semibold text-foreground">Label printing workstation</h3>
            <p className="text-xs text-muted-foreground">
              Prints use the printer selected here. If none is selected, a printer named
              ZDesigner/Zebra is picked automatically; other printers must be chosen by hand.
              Stored locally in your browser.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={loadingPrinters}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loadingPrinters ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {(qzAvailable === false || (!isZebraAvailable && qzAvailable !== null)) && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            QZ Tray is not reachable on this workstation. Install and start QZ Tray, then click
            Refresh.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="ws_printer">Printer</Label>
        <div className="flex gap-2">
          {printers.length > 0 ? (
            <Select
              value={workstation.printerName ?? AUTO_PICK}
              onValueChange={(v) =>
                setWorkstation((prev) =>
                  v === AUTO_PICK
                    ? {
                        ...prev,
                        ...(() => {
                          const c = resolvePrinterChoice({ printers, stored: null, storedSource: null });
                          return { printerName: c.printer, printerSource: c.printer ? ("auto" as const) : null };
                        })(),
                      }
                    : { ...prev, printerName: v, printerSource: "manual" },
                )
              }
            >
              <SelectTrigger id="ws_printer">
                <SelectValue placeholder="Pick a printer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO_PICK}>Auto-pick a Zebra printer</SelectItem>
                {workstation.printerName && !printers.includes(workstation.printerName) && (
                  <SelectItem value={workstation.printerName}>
                    {workstation.printerName} (not found now)
                  </SelectItem>
                )}
                {printers.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <input
              id="ws_printer"
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Click Refresh to list printers, or type a printer name"
              value={workstation.printerName ?? ""}
              onChange={(e) =>
                setWorkstation((prev) => ({
                  ...prev,
                  printerName: e.target.value || null,
                  printerSource: e.target.value ? "manual" : null,
                }))
              }
            />
          )}
        </div>
        {workstation.printerName ? (
          <p className="text-xs text-muted-foreground">
            {workstation.printerSource === "auto" ? "Auto-selected: " : "Manually selected: "}
            <span className="font-mono">{workstation.printerName}</span>
            {workstation.printerSource === "auto" && " (matches Zebra)"}
            {workstation.printerSource !== "auto" &&
              !isLabelPrinterName(workstation.printerName) &&
              " — doesn't look like a Zebra; you'll be asked to confirm before the first print."}
          </p>
        ) : (
          printers.length > 0 && (
            <p className="text-xs text-destructive">No Zebra printer found — pick a printer.</p>
          )
        )}
      </div>

      <div className="space-y-1.5 max-w-xs">
        <Label htmlFor="ws_size">Label size override</Label>
        <Select
          value={workstation.labelSizeOverride ?? USE_COMPANY_DEFAULT}
          onValueChange={(v) =>
            setWorkstation((prev) => ({
              ...prev,
              labelSizeOverride: v === USE_COMPANY_DEFAULT ? null : (v as LabelSizeId),
            }))
          }
        >
          <SelectTrigger id="ws_size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={USE_COMPANY_DEFAULT}>
              Use company default ({labelSizeLabel(companySettings.defaultLabelSize)})
            </SelectItem>
            {ALL_LABEL_SIZES.map((s) => (
              <SelectItem key={s} value={s}>
                {labelSizeOptionLabel(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Effective size for prints from this workstation:{" "}
          <span className="font-mono">{labelSizeLabel(effectiveSize)}</span>
        </p>
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-sm font-medium text-foreground mb-2">Test prints</p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => runTestPrint("spare-part")}
            disabled={!canTest}
          >
            Spare part
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => runTestPrint("storage-bin")}
            disabled={!canTest}
          >
            Storage bin
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => runTestPrint("passport")}
            disabled={!canTest}
          >
            Passport
          </Button>
        </div>
      </div>
    </div>
  );
}
